<template>
  <transition name="modal-fade">
    <div class="modal-overlay">
      <div
        class="modal-body-container change-renewal-date-modal"
        role="dialog"
        aria-labelledby="modalTitle"
        aria-describedby="change renewal date modal"
      >
        <button
          type="button"
          class="btn-close"
          @click="close(false)"
          aria-label="Close modal"
          :disabled="showButtonSpinner"
        >
          &#215;
        </button>
        <!-- pick date -->
        <div v-if="activeStep === STEPS.PICK_DATE" class="modal-content-wrapper pick-date">
          <h4>
            {{ $t("billingAndSubscription.changeRenewalDate.pickDate.heading") }}
          </h4>
          <p class="sub-text">
            {{ $t("billingAndSubscription.changeRenewalDate.pickDate.subText") }}
          </p>
          <p
            class="bold"
            v-html="
              $t('billingAndSubscription.changeRenewalDate.pickDate.content1', {
                currentRenewalDate,
              })
            "
          />
          <p
            class="bold"
            v-html="$t('billingAndSubscription.changeRenewalDate.pickDate.content2')"
          />
          <div>
            <select v-model="selectedDay">
              <option v-bind:key="day.value" v-for="day in daysList" :value="day.value">
                {{ day.label }}
              </option>
            </select>
            <span>{{
              $t(
                isCurrentBillingCycleMonthly
                  ? "billingAndSubscription.changeRenewalDate.dayPickerLabelMonthly"
                  : "billingAndSubscription.changeRenewalDate.dayPickerLabelNonMonthly",
                {
                  billingFrequencyLabel,
                }
              )
            }}</span>
          </div>
          <div class="flex action-buttons">
            <titan-button @click="onDatePick">
              {{ $t("billingAndSubscription.changeRenewalDate.pickDate.primaryBtn") }}
            </titan-button>
          </div>
        </div>

        <!-- confirm date -->
        <div
          v-else-if="activeStep === STEPS.CONFIRM_DATE"
          class="modal-content-wrapper confirm-step"
        >
          <h4>
            {{ $t("billingAndSubscription.changeRenewalDate.confirmDate.heading") }}
          </h4>
          <p
            class="bold firstLetterInCap"
            v-html="
              $t(
                isCurrentBillingCycleMonthly
                  ? 'billingAndSubscription.changeRenewalDate.confirmDate.subHeadingMonthly'
                  : 'billingAndSubscription.changeRenewalDate.confirmDate.subHeadingNonMonthly',
                {
                  selectedDayLabel,
                  billingFrequencyLabel,
                }
              )
            "
          />
          <p v-html="$t('billingAndSubscription.changeRenewalDate.confirmDate.content')" />
          <span v-if="apiError" class="error block">{{ this.apiError }}</span>
          <div class="flex action-buttons">
            <titan-button @click="onRenewalDateConfirm" :loading="showButtonSpinner">
              {{ $t("billingAndSubscription.changeRenewalDate.confirmDate.primaryBtn") }}
            </titan-button>
            <titan-button type="transparent" @click="onDateCancel">
              {{ $t("billingAndSubscription.changeRenewalDate.confirmDate.secondaryBtn") }}
            </titan-button>
          </div>
        </div>

        <!-- success -->
        <div v-else-if="activeStep === STEPS.SUCCESS" class="modal-content-wrapper success-step">
          <img :src="successImg" alt="change renewal date success" />
          <p
            class="firstLetterInCap"
            v-html="
              $t(
                isCurrentBillingCycleMonthly
                  ? 'billingAndSubscription.changeRenewalDate.success.contentMonthly'
                  : 'billingAndSubscription.changeRenewalDate.success.contentNonMonthly',
                {
                  selectedDayLabel,
                  billingFrequencyLabel,
                }
              )
            "
          />
          <span class="badge">{{
            $t("billingAndSubscription.changeRenewalDate.success.badge")
          }}</span>
          <div class="flex action-buttons">
            <titan-button @click="close">
              {{ $t("billingAndSubscription.changeRenewalDate.success.primaryBtn") }}
            </titan-button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>
<script>
import * as helper from "../../helpers";
import { mixin as clickaway } from "vue-clickaway";
import { mapGetters } from "vuex";
import {
  generateDaysArray,
  deriveCustomRenewalDate,
  absoluteDayDiff,
  formatDate,
  DATE_FORMATS,
} from "@/helpers/date";
import billingService from "@/services/billing/billing.service";
import { BILLING_FREQUENCY } from "@/helpers/const";
import bundleEvents from "@/telemetry/bundleEvents";
import successImg from "@/assets/success-2.svg";

const STEPS = {
  PICK_DATE: "pick_date",
  CONFIRM_DATE: "confirm_date",
  SUCCESS: "success",
};
const defaultInitialStep = STEPS.PICK_DATE;
const daysList = generateDaysArray(28);

export default {
  name: "ChangeRenewalDate",
  mixins: [clickaway],
  data() {
    return {
      STEPS,
      daysList,
      activeStep: defaultInitialStep,
      selectedDay: daysList[0].value,
      showButtonSpinner: false,
      apiError: "",
      successImg,
    };
  },
  computed: {
    ...mapGetters({
      products: "products",
      currentPlan: "currentPlanDetails",
      mailPlanDetails: "mail/currentProductDetails",
      sitePlanDetails: "site/currentProductDetails",
    }),
    selectedDayLabel() {
      return this.daysList.find(day => day.value === this.selectedDay).label;
    },
    billingFrequencyLabel() {
      return this.$t(`billingCycles.${this.currentPlan.renewalBillingCycle.replace("ly", "")}`);
    },
    currentRenewalDate() {
      return helper.formatDate(this.currentPlan.expiresOn);
    },
    isCurrentBillingCycleMonthly() {
      return this.currentPlan.renewalBillingCycle === BILLING_FREQUENCY.MONTHLY;
    },
  },
  methods: {
    close() {
      this.$emit("close");
    },
    changeStep(step) {
      this.activeStep = step;
    },
    onDatePick() {
      this.changeStep(STEPS.CONFIRM_DATE);
    },
    async onRenewalDateConfirm() {
      try {
        const now = new Date();
        const currentExpiryDate = new Date(this.currentPlan.expiresOn);
        const _hasMailSuiteProduct = helper.hasMailSuiteProduct(this.products);
        const _hasSiteProduct = helper.hasSiteProduct(this.products);
        const currentMailPlanExpiryDate = _hasMailSuiteProduct
          ? new Date(this.mailPlanDetails.expiry)
          : null;
        const currentSitePlanExpiryDate = _hasSiteProduct
          ? new Date(this.sitePlanDetails.expiry)
          : null;
        const numberOfRemainingTrialDaysFromNow = absoluteDayDiff(now, currentExpiryDate);
        console.log({ _hasMailSuiteProduct, _hasSiteProduct });
        const newExpiryFormattedDate = deriveCustomRenewalDate(
          this.selectedDay,
          numberOfRemainingTrialDaysFromNow
        );
        const newExpiryDate = new Date(newExpiryFormattedDate);

        // show loader
        this.showButtonSpinner = true;
        this.apiError = "";

        // make API call
        await billingService.changeBillingDate(
          formatDate(newExpiryFormattedDate, DATE_FORMATS.ISO)
        );
        const additionalFreeDays = absoluteDayDiff(newExpiryDate, currentExpiryDate);

        // inform medusa
        bundleEvents.logRenewalDateChanged({
          day_of_month_selected: this.selectedDay,
          additional_free_days: additionalFreeDays,
          old_renewal_date: helper.formatDate(currentExpiryDate),
          renewal_date: helper.formatDate(newExpiryDate),
          ...helper.returnObjectIfValueExists(
            "old_mail_expiry",
            _hasMailSuiteProduct ? currentMailPlanExpiryDate.getTime() : undefined
          ),
          ...helper.returnObjectIfValueExists(
            "old_site_expiry",
            _hasSiteProduct ? currentSitePlanExpiryDate.getTime() : undefined
          ),
          ...helper.returnObjectIfValueExists(
            "mail_expiry",
            _hasMailSuiteProduct ? newExpiryDate.getTime() : undefined
          ),
          ...helper.returnObjectIfValueExists(
            "site_expiry",
            _hasSiteProduct ? newExpiryDate.getTime() : undefined
          ),
        });

        // sync current plan
        this.$emit("fetchCurrentPlan");

        // show success
        this.changeStep("success");
      } catch (err) {
        this.apiError = err?.data?.desc || this.$t("manageSubscriptions.commonError");
      } finally {
        this.showButtonSpinner = false;
      }
    },
    onDateCancel() {
      this.close();
    },
  },
};
</script>
<style lang="scss">
@import "../../styles/variables.scss";
.change-renewal-date-modal {
  width: 90%;
  min-width: auto;
  max-width: 520px;
  padding: 30px;

  .firstLetterInCap {
    text-transform: lowercase;

    &::first-letter {
      text-transform: uppercase;
    }
  }

  select {
    width: 90px;
    height: 40px;
    border-radius: 4px;
    border: solid 1px var(--lineSepaCol);
    display: inline-block;
    outline: none;
    padding: 8px;
    margin-right: 8px;

    & + span {
      text-transform: lowercase;
      font-size: 15px;
    }
  }

  h4 {
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    color: var(--primaryTextCol);
    margin-bottom: 8px;

    & + p {
      font-size: 15px;
      font-weight: 400;

      &.sub-text {
        color: var(--tertiaryTextCol);
      }
    }
  }

  .pick-date {
    p + p {
      margin-top: 24px;
      margin-bottom: 12px;

      & + p {
        margin-top: 0;
        margin-bottom: 6px;
      }
    }
  }

  .confirm-step {
    h4 + p {
      margin: 18px 0 16px;
    }
  }

  p {
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
    color: var(--primaryTextCol);

    &.bold {
      font-weight: bold;
    }
  }

  .action-buttons {
    margin-top: 40px;

    button {
      outline: none;
      & + button {
        margin-left: 16px;
      }
    }
  }

  .success-step {
    text-align: center;
    max-width: 300px;
    margin: 0 auto 22px;

    img {
      width: 120px;
      height: auto;
      margin: 28px auto;

      & + p {
        margin-bottom: 12px;
      }
    }

    .action-buttons {
      margin-top: 28px;

      button {
        width: 100%;
      }
    }

    .badge {
      display: inline-flex;
      padding: 7px 20px;
      justify-content: center;
      align-items: center;
      font-size: 13px;
      font-weight: 600;
      text-transform: uppercase;
      border-radius: 200px;
      color: var(--greenBadge);
      background: var(--badgeBg);
    }
  }

  @media screen and (max-width: $responsive-breakpoint) {
    .confirm-step,
    .success-step {
      .action-buttons {
        flex-direction: column;
        gap: 16px;

        button {
          width: 100%;
          margin: 0;
        }
      }
    }
  }
}
</style>
